// Timer · Stopwatch · Pomodoro.
import { el, btn, input, wrap, beep, sel, field, announce } from "./ui.js";
import { bump, today, weekSum, fmtMinutes, DAYS_KEY } from "../shared/activity.js";
import { subscribe, readJSON } from "../shared/store.js";
import { MUSIC_PRESETS } from "../shared/settings-schema.js";
import { syncPlan } from "../shared/commons.js";

const pad = (n) => (n < 10 ? "0" + n : "" + n);

export function timer(root) {
	const s = wrap(root); s.classList.add("rv-timer");
	const tabs = el("div", "rv-timer-tabs");
	tabs.setAttribute("role", "group");
	tabs.setAttribute("aria-label", "Mode");
	const body = el("div", "rv-timer-body");
	s.append(tabs, body);
	let cleanup = null;
	const modes = [["stopwatch", "Stopwatch"], ["timer", "Timer"], ["pomodoro", "Pomodoro"]];
	function show(mode) {
		if (cleanup) { cleanup(); cleanup = null; }
		[...tabs.children].forEach((b) => b.classList.toggle("active", b.dataset.m === mode));
		body.textContent = "";
		cleanup = (mode === "stopwatch" ? stopwatch : mode === "timer" ? countdown : pomodoro)(body);
	}
	modes.forEach(([m, label]) => { const b = btn(label, () => show(m), "rv-chip"); b.dataset.m = m; tabs.appendChild(b); });
	show(location.hash === "#pomodoro" ? "pomodoro" : "stopwatch"); // #pomodoro: straight to focus mode
	// (tools tabs are reloaded fresh; no global teardown needed)
}

function stopwatch(body) {
	const disp = el("div", "rv-timer-display", "00:00.00");
	let start = 0, acc = 0, raf = null, running = false;
	const laps = el("ol", "rv-timer-laps");
	function fmt(ms) { const t = Math.floor(ms); const m = Math.floor(t / 60000), s = Math.floor((t % 60000) / 1000), cs = Math.floor((t % 1000) / 10); return pad(m) + ":" + pad(s) + "." + pad(cs); }
	function tick() { disp.textContent = fmt(acc + (running ? performance.now() - start : 0)); raf = requestAnimationFrame(tick); }
	const go = btn("Start", () => {
		running = !running;
		if (running) { start = performance.now(); go.textContent = "Stop"; go.classList.add("op"); }
		else { acc += performance.now() - start; go.textContent = "Start"; go.classList.remove("op"); }
	}, "rv-tbtn rv-tbtn-accent");
	const lap = btn("Lap", () => { if (running || acc) { const li = el("li", null, fmt(acc + (running ? performance.now() - start : 0))); laps.prepend(li); } }, "rv-tbtn");
	const reset = btn("Reset", () => { running = false; acc = 0; start = 0; go.textContent = "Start"; go.classList.remove("op"); laps.textContent = ""; }, "rv-tbtn");
	body.append(disp, el("div", "rv-timer-row"), laps);
	body.querySelector(".rv-timer-row").append(go, lap, reset);
	tick();
	return () => cancelAnimationFrame(raf);
}

function countdown(body) {
	const mins = input("number", { value: "5", min: "0", max: "999" }); mins.classList.add("rv-timer-num");
	const secs = input("number", { value: "0", min: "0", max: "59" }); secs.classList.add("rv-timer-num");
	mins.setAttribute("aria-label", "Minutes");
	secs.setAttribute("aria-label", "Seconds");
	const disp = el("div", "rv-timer-display", "05:00");
	let remaining = 300000, planned = 300000, end = 0, running = false, iv = null;
	function fmt(ms) { const t = Math.max(0, Math.ceil(ms / 1000)); return pad(Math.floor(t / 60)) + ":" + pad(t % 60); }
	function refresh() { disp.textContent = fmt(remaining); }
	function setFromInputs() { remaining = planned = (parseInt(mins.value || "0") * 60 + parseInt(secs.value || "0")) * 1000; refresh(); }
	mins.addEventListener("input", () => { if (!running) setFromInputs(); });
	secs.addEventListener("input", () => { if (!running) setFromInputs(); });
	const go = btn("Start", () => {
		if (running) { running = false; clearInterval(iv); remaining = end - performance.now(); go.textContent = "Start"; go.classList.remove("op"); return; }
		if (remaining <= 0) setFromInputs();
		running = true; end = performance.now() + remaining; go.textContent = "Pause"; go.classList.add("op");
		iv = setInterval(() => {
			remaining = end - performance.now(); refresh();
			if (remaining <= 0) { clearInterval(iv); running = false; focused(Math.floor(planned / 60000), 10); go.textContent = "Start"; go.classList.remove("op"); disp.classList.add("done"); beep(700, 880); announce("Time's up"); setTimeout(() => disp.classList.remove("done"), 1500); }
		}, 100);
	}, "rv-tbtn rv-tbtn-accent");
	const reset = btn("Reset", () => { running = false; clearInterval(iv); setFromInputs(); go.textContent = "Start"; go.classList.remove("op"); }, "rv-tbtn");
	const setRow = el("div", "rv-timer-row"); setRow.append(mins, el("span", "rv-timer-colon", ":"), secs);
	const ctl = el("div", "rv-timer-row"); ctl.append(go, reset);
	body.append(disp, setRow, ctl);
	refresh();
	return () => clearInterval(iv);
}

// A finished focus block goes into the study ledger (counts and minutes only). Countdowns
// count from 10 minutes up; the stopwatch never counts (open-ended).
function focused(minutes, min = 1) {
	if (minutes < min) return;
	bump("fo");
	bump("fm", minutes);
}
// "Today: 50 min · This week: 3 h 10 min", empty while both are zero.
export function focusLine(t = today().fm | 0, w = weekSum("fm")) {
	return t || w ? "Today: " + fmtMinutes(t) + " · This week: " + fmtMinutes(w) : "";
}

// Sound while focusing (docs/HOME-2026-10.md, F4 stage B): the app plays a soundscape while a
// work phase runs and dims it on breaks. Only inside the app (the page has a parent to ask).
// The choice is the focusSound setting, written straight into the stored settings (the tools
// bundle carries no settings store) so the app and Settings pick it up.
const inApp = () => { try { return window.parent !== window; } catch { return false; } };
const SETTINGS = "revault:settings";
function focusSound() { const s = readJSON(SETTINGS, null); const v = s && s.focusSound; return MUSIC_PRESETS.some((m) => m.id === v) ? v : "off"; }
function setFocusSound(v) {
	try { const s = readJSON(SETTINGS, null); localStorage.setItem(SETTINGS, JSON.stringify(Object.assign(s && typeof s === "object" ? s : {}, { focusSound: v }))); } catch { /* storage full or off */ }
}
// left: seconds left in this phase, so Island Mode can show the countdown. With no sound picked the
// app still hears the phase (as preset "off"), and plays nothing.
function tellApp(phase, left) {
	if (!inApp()) return;
	const end = phase === "stop" ? 0 : Date.now() + Math.max(0, left || 0) * 1000;
	try { window.parent.postMessage(phase === "stop" ? { type: "revault-focus", phase: "stop" } : { type: "revault-focus", phase, preset: focusSound(), end }, location.origin); } catch { /* no parent */ }
}

// "Start with everyone" (shared/commons.js): focus starts at :00 and :30 by the clock, together with
// everyone else who picked it. Saved in the stored settings like the sound.
const focusSync = () => { const s = readJSON(SETTINGS, null); return !!(s && s.focusSync === true); };
function setFocusSync(v) {
	try { const s = readJSON(SETTINGS, null); localStorage.setItem(SETTINGS, JSON.stringify(Object.assign(s && typeof s === "object" ? s : {}, { focusSync: !!v }))); } catch { /* storage full or off */ }
}
const clockAt = (ms) => new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

function pomodoro(body) {
	const WORK = (self.__rvPomoSecs | 0) || 25 * 60, BREAK = 5 * 60; // __rvPomoSecs: browser-check hook
	let phase = "work", remaining = WORK, running = false, iv = null, end = 0, done = 0;
	let workLen = WORK; // this focus block's length in seconds (shorter when joining a group block late)
	let waitUntil = 0; // waiting for the next :00 or :30
	const label = el("div", "rv-timer-phase", "Focus");
	const disp = el("div", "rv-timer-display", fmt(WORK));
	const count = el("div", "rv-tnote rv-timer-sitting", "0 sessions this sitting");
	const stats = el("div", "rv-tnote rv-timer-focus");
	const paintStats = () => { stats.textContent = focusLine(); stats.hidden = !stats.textContent; };
	const unsub = subscribe(DAYS_KEY, paintStats);
	paintStats();
	function fmt(s) { s = Math.max(0, Math.ceil(s)); return pad(Math.floor(s / 60)) + ":" + pad(s % 60); }
	function refresh() {
		if (waitUntil) { const left = (waitUntil - Date.now()) / 1000; disp.textContent = fmt(left); label.textContent = "Starts at " + clockAt(waitUntil) + " with everyone"; return; }
		disp.textContent = fmt(remaining); label.textContent = phase === "work" ? "Focus" : "Break";
	}
	// A group block runs by the wall clock, so it ends with everyone else's.
	function syncWork() { const p = syncPlan(Date.now()); remaining = Math.max(0, (p.end - Date.now()) / 1000); workLen = Math.round(remaining); }
	function switchPhase() {
		if (phase === "work") { done++; count.textContent = done + (done === 1 ? " session" : " sessions") + " this sitting"; focused(workLen === WORK ? 25 : Math.round(workLen / 60), 10); paintStats(); phase = "break"; remaining = BREAK; }
		else { phase = "work"; remaining = WORK; workLen = WORK; if (focusSync()) syncWork(); }
		beep(600, phase === "work" ? 660 : 990); refresh(); announce(phase === "work" ? "Break over. Back to focus." : "Focus done. Take a 5 minute break."); end = performance.now() + remaining * 1000; tellApp(phase, remaining);
	}
	function run() {
		running = true; end = performance.now() + remaining * 1000; go.textContent = "Pause"; go.classList.add("op"); tellApp(phase, remaining);
		iv = setInterval(() => { remaining = (end - performance.now()) / 1000; refresh(); if (remaining <= 0) switchPhase(); }, 200);
	}
	function stopWait() { waitUntil = 0; clearInterval(iv); go.textContent = "Start"; go.classList.remove("op"); refresh(); }
	const go = btn("Start", () => {
		if (waitUntil) { stopWait(); announce("Not waiting for the group session."); return; }
		if (running) { running = false; clearInterval(iv); remaining = (end - performance.now()) / 1000; go.textContent = "Start"; go.classList.remove("op"); tellApp("stop"); return; }
		// A fresh focus block with "Start with everyone" on: join the block that started up to 5
		// minutes ago, or wait for the next one.
		if (focusSync() && phase === "work" && remaining >= WORK - 1) {
			const p = syncPlan(Date.now());
			if (p.wait > 0) {
				waitUntil = p.start;
				go.textContent = "Cancel"; go.classList.add("op");
				announce("Starting at " + clockAt(p.start) + " with everyone.");
				iv = setInterval(() => { if (Date.now() >= waitUntil) { waitUntil = 0; clearInterval(iv); syncWork(); refresh(); beep(400, 660); announce("Focus started with everyone."); run(); } else refresh(); }, 250);
				refresh();
				return;
			}
			syncWork();
		}
		run();
	}, "rv-tbtn rv-tbtn-accent");
	const reset = btn("Reset", () => { const was = running; running = false; waitUntil = 0; clearInterval(iv); phase = "work"; remaining = WORK; workLen = WORK; go.textContent = "Start"; go.classList.remove("op"); refresh(); if (was) tellApp("stop"); }, "rv-tbtn");
	const ctl = el("div", "rv-timer-row"); ctl.append(go, reset);
	const sync = input("checkbox", { checked: focusSync() });
	sync.addEventListener("change", () => { setFocusSync(sync.checked); if (!sync.checked && waitUntil) stopWait(); });
	const syncRow = el("label", "rv-timer-sync");
	syncRow.append(sync, el("span", null, "Start with everyone at :00 and :30"));
	body.append(label, disp, ctl, syncRow, count, stats);
	let leave = null;
	if (inApp()) {
		const pick = sel([["off", "Off"], ...MUSIC_PRESETS.map((m) => [m.id, m.label])], focusSound());
		pick.classList.add("rv-timer-sound");
		pick.addEventListener("change", () => { setFocusSound(pick.value); if (running) tellApp(phase, (end - performance.now()) / 1000); });
		body.append(field("Sound while focusing", pick));
		leave = () => { if (running) tellApp("stop"); };
		addEventListener("pagehide", leave);
	}
	refresh();
	return () => { clearInterval(iv); unsub(); if (leave) { leave(); removeEventListener("pagehide", leave); } };
}
